/**
 * liveStatus.js (client) — canonical machine live-status helpers.
 *
 * Mirrors server/utils/liveStatus.js. Status priority (highest activity wins):
 *   ENGINE_ON  engine running        (engine_speed/SPN 190 in (0, 4000] RPM)
 *   IGN_ON     ignition on, engine off (ign === 1)
 *   GPS_ON     ign off, device alive & located (valid GPS fix, fresh frame)
 *   OFF        no fresh telemetry     (vehicle off / device asleep / offline)
 *
 * The dashboard treats these four codes as the single status vocabulary. Any
 * legacy value from older endpoints is normalised into one of them.
 */

// A machine with no frame newer than this is OFF. Matches the server default.
export const OFFLINE_MS = 10 * 60 * 1000;
const ENGINE_RPM_MAX = 4000;

export const STATUS_CODES = ['ENGINE_ON', 'IGN_ON', 'GPS_ON', 'OFF'];

// label, MUI Chip color, and a raw hex (for map markers / custom dots).
export const STATUS_META = {
    ENGINE_ON: { label: 'Engine On', color: 'success', hex: '#22c55e' },
    IGN_ON: { label: 'Ignition On', color: 'warning', hex: '#f59e0b' },
    GPS_ON: { label: 'GPS On', color: 'info', hex: '#3b82f6' },
    OFF: { label: 'Vehicle Off', color: 'default', hex: '#9ca3af' },
};

// Map every spelling we've ever used → canonical code.
const ALIASES = {
    ENGINE_ON: 'ENGINE_ON', ENGINE: 'ENGINE_ON', ACTIVE: 'ENGINE_ON', RUNNING: 'ENGINE_ON',
    IGN_ON: 'IGN_ON', IGNITION: 'IGN_ON', KEY_ON: 'IGN_ON', IGNITION_ON: 'IGN_ON',
    GPS_ON: 'GPS_ON', GPS: 'GPS_ON', GPS_DEVICE_ON: 'GPS_ON', CONNECTED: 'GPS_ON',
    OFF: 'OFF', VEHICLE_OFF: 'OFF', KEY_OFF: 'OFF', OFFLINE: 'OFF', NO_DATA: 'OFF', '': 'OFF',
};

export const normalizeStatus = (raw) => {
    if (!raw) return 'OFF';
    const key = String(raw).toUpperCase().replace(/\s+/g, '_');
    return ALIASES[key] || 'OFF';
};

export const statusLabel = (code) => (STATUS_META[normalizeStatus(code)] || STATUS_META.OFF).label;
export const statusColor = (code) => (STATUS_META[normalizeStatus(code)] || STATUS_META.OFF).color;
export const statusHex = (code) => (STATUS_META[normalizeStatus(code)] || STATUS_META.OFF).hex;

const num = (v) => {
    if (v === null || v === undefined || v === '') return NaN;
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : NaN;
};

const truthyIgn = (v) => v === 1 || v === '1' || v === true || v === 'on' || v === 'ON';

/**
 * Compute a device's live status from its (possibly mixed-shape) telemetry
 * fields plus a freshness timestamp. Used to reconcile snapshots and to apply
 * client-side staleness between socket frames.
 *
 * @param {object} d   device/telemetry object (flattened — many field aliases supported)
 * @param {object} opts { now, offlineMs }
 */
export const computeDeviceStatus = (d = {}, opts = {}) => {
    const now = opts.now || Date.now();
    const offlineMs = opts.offlineMs != null ? opts.offlineMs : OFFLINE_MS;

    const loc = d.location || {};
    const tsRaw = d.__liveTs || d.last_seen || d.recorded_at || d.timestamp || loc.time;
    const tsMs = tsRaw ? new Date(tsRaw).getTime() : NaN;
    const stale = !Number.isFinite(tsMs) || (now - tsMs) > offlineMs;
    if (stale) return 'OFF';

    const rpm = num(d.engine_speed != null ? d.engine_speed
        : (d['190'] != null ? d['190'] : d.current_engine_speed));
    const engineOn = Number.isFinite(rpm) && rpm > 0 && rpm <= ENGINE_RPM_MAX;

    const ign = d.ign != null ? d.ign : (d.ignition != null ? d.ignition : loc.ign);
    const ignOn = truthyIgn(ign);

    const lat = num(d.lat != null ? d.lat : (d.latitude != null ? d.latitude : (loc.lat != null ? loc.lat : loc.latitude)));
    const lng = num(d.lng != null ? d.lng : (d.longitude != null ? d.longitude : (loc.lon != null ? loc.lon : loc.longitude)));
    const gpsOn = Number.isFinite(lat) && Number.isFinite(lng) &&
        !(lat === 0 && lng === 0) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;

    if (engineOn) return 'ENGINE_ON';
    if (ignOn) return 'IGN_ON';
    if (gpsOn) return 'GPS_ON';
    return 'OFF';
};

/**
 * Resolve the status to display for a device: prefer a server/socket-provided
 * code when present, but force OFF if the newest known frame is stale.
 */
export const resolveDeviceStatus = (d = {}, now = Date.now()) => {
    const loc = d.location || {};
    const tsRaw = d.__liveTs || d.last_seen || d.recorded_at || d.timestamp || loc.time;
    const tsMs = tsRaw ? new Date(tsRaw).getTime() : NaN;
    if (!Number.isFinite(tsMs) || (now - tsMs) > OFFLINE_MS) return 'OFF';
    if (d.live_status) return normalizeStatus(d.live_status);
    return computeDeviceStatus(d, { now });
};
